import { useState, useMemo, useCallback } from "react";
import { norm } from "./utils";
import { analytics, triggerLogEvent } from "../../../config/firebase";

/**
 * Hook for AppTableFilters: resolves config, state, and all popover/handler logic.
 */
export function useAppTableFilters({
  filtersConfig: configProp,
  filterState: stateProp,
  onFilterChange,
  filters,
  filterOptions = {},
  selected = {},
  onSelectionChange,
  buildConfigFromLegacy,
  categories,
}) {
  const filtersConfig =
    configProp?.length > 0
      ? configProp
      : buildConfigFromLegacy(filters, filterOptions, categories);

  const [internalSelection, setInternalSelection] = useState({});
  const isControlled = onFilterChange != null || onSelectionChange != null;
  const filterState = isControlled
    ? (stateProp ?? selected)
    : internalSelection;

  const [activeId, setActiveId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [amountLocal, setAmountLocal] = useState({ min: "", max: "" });

  const legacyOnChange = useCallback(
    (id, value) => onSelectionChange?.({ ...filterState, [id]: value }),
    [onSelectionChange, filterState],
  );
  const internalOnChange = useCallback((id, value) => {
    setInternalSelection((prev) => ({ ...prev, [id]: value }));
  }, []);
  const onFilterChangeFn =
    onFilterChange ?? (onSelectionChange ? legacyOnChange : internalOnChange);

  const activeConfig = useMemo(
    () => filtersConfig.find((f) => f.id === activeId),
    [filtersConfig, activeId],
  );
  const isOpen = Boolean(anchorEl && activeConfig);

  const handleOpen = useCallback(
    (id, event) => {
      if (activeId === id) {
        setActiveId(null);
        setAnchorEl(null);
        setSearchQuery("");
        return;
      }
      triggerLogEvent(analytics, "filter_opened", { filter_id: id });
      setActiveId(id);
      setAnchorEl(event.currentTarget);
      setSearchQuery("");
      const cfg = filtersConfig.find((f) => f.id === id);
      if (cfg?.type === "amount" && filterState.amount) {
        setAmountLocal({
          min: filterState.amount.min ?? "",
          max: filterState.amount.max ?? "",
        });
      }
    },
    [activeId, filtersConfig, filterState],
  );

  const handleClose = useCallback(() => {
    setActiveId(null);
    setAnchorEl(null);
    setSearchQuery("");
  }, []);

  const handleMultiSelect = useCallback(
    (optionValue) => {
      const current = filterState[activeId] ?? [];
      const arr = Array.isArray(current) ? current : [];
      const next = arr.includes(optionValue)
        ? arr.filter((v) => v !== optionValue)
        : [...arr, optionValue];
      triggerLogEvent(analytics, "filter_option_toggled", { filter_id: activeId, value: optionValue });
      onFilterChangeFn(activeId, next);
    },
    [activeId, filterState, onFilterChangeFn],
  );

  const handleSingleSelect = useCallback(
    (optionValue) => {
      triggerLogEvent(analytics, "filter_option_selected", { filter_id: activeId, value: optionValue });
      onFilterChangeFn(activeId, optionValue);
      handleClose();
    },
    [activeId, onFilterChangeFn, handleClose],
  );

  const handleAmountApply = useCallback(() => {
    triggerLogEvent(analytics, "filter_amount_applied", { filter_id: activeId, min: amountLocal.min, max: amountLocal.max });
    onFilterChangeFn(activeId, { min: amountLocal.min, max: amountLocal.max });
    handleClose();
  }, [activeId, amountLocal, onFilterChangeFn, handleClose]);

  // Search renders as an always-visible inline box (not a dropdown), so it
  // applies directly on every keystroke rather than requiring an explicit apply step.
  const handleKeywordsChange = useCallback(
    (id, value) => {
      triggerLogEvent(analytics, "filter_keywords_applied", { filter_id: id, keywords: value });
      onFilterChangeFn(id, value);
    },
    [onFilterChangeFn],
  );

  const handleDateApply = useCallback(
    (startDate, endDate) => {
      triggerLogEvent(analytics, "filter_date_applied", { filter_id: activeId, startDate, endDate });
      onFilterChangeFn(activeId, { startDate, endDate });
      handleClose();
    },
    [activeId, onFilterChangeFn, handleClose],
  );

  const options = activeConfig?.options ?? [];
  const normalizedOptions = useMemo(() => options.map(norm), [options]);

  const selectedSet = useMemo(
    () =>
      new Set(
        Array.isArray(filterState[activeId]) ? filterState[activeId] : [],
      ),
    [filterState, activeId],
  );
  const selectedSingle =
    activeConfig?.type === "sort" ? filterState[activeId] : null;

  const selectedDate =
    activeConfig?.type === "date" ? (filterState[activeId] ?? null) : null;

  return {
    filtersConfig,
    filterState,
    activeId,
    anchorEl,
    isOpen,
    activeConfig,
    handleOpen,
    handleClose,
    normalizedOptions,
    selectedSet,
    selectedSingle,
    amountLocal,
    setAmountLocal,
    searchQuery,
    setSearchQuery,
    handleMultiSelect,
    handleSingleSelect,
    handleAmountApply,
    handleKeywordsChange,
    handleDateApply,
    selectedDate,
  };
}
